import { create } from "zustand";
import type { AttachmentRef, ProviderSelection } from "@rivets/core";
import { client } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import { useChatComposers } from "./chatComposerStore";
import { useDraftChats, type DraftChat } from "./draftChatStore";
import { handoffDraftKey, useHandoffDrafts } from "./handoffDraftStore";
import { deliverPendingSend, hasSendsInFlight, recoverPendingSendChat, usePendingSends, type PendingSend } from "./pendingSends";
import { useChatStore } from "./chatStore";
import type { PendingAttachment } from "./useAttachments";
import { useNewTaskDraft, type NewTaskDraft } from "@/features/worktrees/newTaskDraftStore";

/**
 * Unsent work survives everything (CONTRACT C3, web #2).
 *
 * Composer text, the attachments that finished uploading (their server refs,
 * not the bytes), open "New chat" and handoff drafts, the create-task prompt
 * and every send the server has not echoed are written to localStorage, per
 * signed-in member and organization. A reload, a discarded tab, an
 * organization switch or a protocol reload reads them back: drafts reappear
 * where they were, an in-flight send goes again under its own
 * `clientRequestId` (the server answers it as the same request), an accepted
 * one waits for its echo, and a failed one keeps its Retry.
 *
 * The in-memory stores hold one organization at a time. Writes are coalesced
 * and land a moment after the last change; leaving the page, hiding it and
 * every deliberate reload write at once.
 */

const KEY_PREFIX = "rivets.drafts.v1";
/** Typing is coalesced into one write this long after the last change. */
export const DRAFT_WRITE_DELAY_MS = 300;

export interface PersistedComposer {
  text: string;
  attachments: AttachmentRef[];
}

export interface PersistedNewChat extends PersistedComposer {
  inFront: boolean;
  provider?: ProviderSelection;
  review?: boolean;
}

export interface PersistedHandoff {
  worktreeId: string;
  sourceChatId: string;
  text: string;
  provider?: ProviderSelection;
}

export interface PersistedDrafts {
  /** chatId → the reply left in that chat's composer. */
  chats: Record<string, PersistedComposer>;
  /** worktreeId → its open "New chat" tab. */
  newChats: Record<string, PersistedNewChat>;
  handoffs: PersistedHandoff[];
  /** worktreeId → the handoff that was in front of it. */
  handoffFront: Record<string, string>;
  newTask: NewTaskDraft | null;
  /** chatId → messages not yet echoed, oldest first. */
  pendingSends: Record<string, PendingSend[]>;
}

export function draftsKey(userId: string, orgId: string): string {
  return `${KEY_PREFIX}:${userId}:${orgId}`;
}

function storage(): Storage | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}

function readyRefs(attachments: readonly PendingAttachment[]): AttachmentRef[] {
  // Bytes still uploading are not worth keeping; a finished upload's ref is.
  return attachments.flatMap((attachment) => (attachment.ref ? [attachment.ref] : []));
}

/** The trays of composers on screen. A mounted composer keeps its tray in
 * its own state and parks it in the store only when it unmounts, so what it
 * holds is read from here — a reload with the composer open keeps its files. */
const liveTrays = new Map<string, () => readonly PendingAttachment[]>();

/** A composer's tray, readable while it is mounted. `key` is `chat:<chatId>`
 * or `new:<worktreeId>`. Returns the unregister. */
export function registerLiveTray(key: string, read: () => readonly PendingAttachment[]): () => void {
  liveTrays.set(key, read);
  return () => {
    if (liveTrays.get(key) === read) liveTrays.delete(key);
    scheduleWrite();
  };
}

/** A mounted composer's tray changed (an upload finished, a file removed). */
export function trayChanged(): void {
  scheduleWrite();
}

/** What the stores hold now, in the on-disk shape. */
export function snapshotDrafts(): PersistedDrafts {
  const chats: PersistedDrafts["chats"] = {};
  const composers = useChatComposers.getState().composers;
  const chatIds = new Set([
    ...Object.keys(composers),
    ...[...liveTrays.keys()].filter((key) => key.startsWith("chat:")).map((key) => key.slice(5)),
  ]);
  for (const chatId of chatIds) {
    const text = composers[chatId]?.text ?? "";
    const attachments = readyRefs(liveTrays.get(`chat:${chatId}`)?.() ?? composers[chatId]?.attachments ?? []);
    if (text.trim() || attachments.length > 0) chats[chatId] = { text, attachments };
  }
  const newChats: PersistedDrafts["newChats"] = {};
  for (const [worktreeId, draft] of Object.entries(useDraftChats.getState().drafts)) {
    newChats[worktreeId] = {
      text: draft.text,
      attachments: readyRefs(liveTrays.get(`new:${worktreeId}`)?.() ?? draft.attachments),
      inFront: draft.inFront,
      ...(draft.provider ? { provider: draft.provider } : {}),
      ...(draft.review ? { review: true } : {}),
    };
  }
  const handoffState = useHandoffDrafts.getState();
  const handoffs: PersistedHandoff[] = [];
  for (const [key, draft] of Object.entries(handoffState.drafts)) {
    const split = key.indexOf(":");
    if (split < 0) continue;
    handoffs.push({ worktreeId: key.slice(0, split), sourceChatId: key.slice(split + 1), text: draft.text, ...(draft.provider ? { provider: draft.provider } : {}) });
  }
  const pendingSends: PersistedDrafts["pendingSends"] = {};
  for (const [chatId, list] of Object.entries(usePendingSends.getState().byChat)) {
    if (list.length > 0) pendingSends[chatId] = list;
  }
  return {
    chats,
    newChats,
    handoffs,
    handoffFront: { ...handoffState.front },
    newTask: useNewTaskDraft.getState().draft,
    pendingSends,
  };
}

function isEmpty(drafts: PersistedDrafts): boolean {
  return Object.keys(drafts.chats).length === 0 && Object.keys(drafts.newChats).length === 0
    && drafts.handoffs.length === 0 && drafts.newTask === null && Object.keys(drafts.pendingSends).length === 0;
}

export function readDrafts(userId: string, orgId: string): PersistedDrafts | null {
  try {
    const raw = storage()?.getItem(draftsKey(userId, orgId));
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<PersistedDrafts> | null;
    if (!value || typeof value !== "object") return null;
    return {
      chats: value.chats && typeof value.chats === "object" ? value.chats : {},
      newChats: value.newChats && typeof value.newChats === "object" ? value.newChats : {},
      handoffs: Array.isArray(value.handoffs) ? value.handoffs : [],
      handoffFront: value.handoffFront && typeof value.handoffFront === "object" ? value.handoffFront : {},
      newTask: value.newTask && typeof value.newTask.text === "string" ? value.newTask : null,
      pendingSends: value.pendingSends && typeof value.pendingSends === "object" ? value.pendingSends : {},
    };
  } catch {
    return null;
  }
}

function writeDrafts(userId: string, orgId: string, drafts: PersistedDrafts): void {
  const store = storage();
  if (!store) return;
  try {
    if (isEmpty(drafts)) store.removeItem(draftsKey(userId, orgId));
    else store.setItem(draftsKey(userId, orgId), JSON.stringify(drafts));
  } catch {
    // Quota or disabled storage: the drafts stay in memory for this page.
  }
}

/** A finished upload, back in a composer's tray after a reload. Its preview
 * is the uploaded file itself; there are no local bytes any more. */
export function restoredAttachment(orgId: string, ref: AttachmentRef): PendingAttachment {
  return {
    key: `restored-${ref.id}`,
    file: new File([], ref.name ?? "attachment", { type: ref.mediaType }),
    mediaType: ref.mediaType,
    previewUrl: client.attachmentUrl(orgId, ref.id),
    status: "ready",
    ref,
  };
}

let scope: { userId: string; orgId: string } | null = null;

/** Which organization's drafts the stores hold, for the shell to wait on:
 * a composer reads its parked tray when it mounts, so it must not mount
 * before the organization's drafts are in. */
export const useDraftScope = create<{ userId: string | null; orgId: string | null }>(() => ({ userId: null, orgId: null }));
let unsubscribers: Array<() => void> = [];
let writeTimer: ReturnType<typeof setTimeout> | null = null;
/** Set once a deliberate reload or sign-out is under way: nothing more is
 * written (the stores are about to be cleared) and nothing asks to stay. */
let leaving = false;

function revokePreviews() {
  // A restored tray's preview is a server URL; revoking it is a no-op.
  for (const composer of Object.values(useChatComposers.getState().composers)) {
    for (const attachment of composer.attachments) URL.revokeObjectURL(attachment.previewUrl);
  }
  for (const draft of Object.values(useDraftChats.getState().drafts)) {
    for (const attachment of draft.attachments) URL.revokeObjectURL(attachment.previewUrl);
  }
}

/** Puts one organization's saved drafts into the stores. */
function hydrate(userId: string, orgId: string) {
  const saved = readDrafts(userId, orgId);
  useChatComposers.setState({
    composers: Object.fromEntries(Object.entries(saved?.chats ?? {}).map(([chatId, composer]) => [chatId, {
      text: composer.text ?? "",
      attachments: (composer.attachments ?? []).map((ref) => restoredAttachment(orgId, ref)),
    }])),
  });
  useDraftChats.setState({
    drafts: Object.fromEntries(Object.entries(saved?.newChats ?? {}).map(([worktreeId, draft]): [string, DraftChat] => [worktreeId, {
      text: draft.text ?? "",
      inFront: draft.inFront === true,
      attachments: (draft.attachments ?? []).map((ref) => restoredAttachment(orgId, ref)),
      ...(draft.provider ? { provider: draft.provider } : {}),
      ...(draft.review ? { review: true } : {}),
    }])),
  });
  useHandoffDrafts.setState({
    drafts: Object.fromEntries((saved?.handoffs ?? []).map((handoff) => [handoffDraftKey(handoff.worktreeId, handoff.sourceChatId), {
      text: handoff.text ?? "",
      ...(handoff.provider ? { provider: handoff.provider } : {}),
    }])),
    front: { ...(saved?.handoffFront ?? {}) },
  });
  useNewTaskDraft.getState().restore(saved?.newTask ?? null);
  const pending = saved?.pendingSends ?? {};
  usePendingSends.getState().restore(pending);
  // A send whose echo this browser already holds is done.
  const chats = useChatStore.getState().chats;
  for (const chatId of Object.keys(pending)) {
    const events = chats[chatId]?.events;
    if (events?.length) usePendingSends.getState().settle(chatId, events);
  }
}

/**
 * How old a restored in-flight send may be and still go again on its own
 * (review #16). The server forgets a `clientRequestId` after 24 h, so one that
 * landed long ago would be posted twice; well before that, a message the
 * reader sent an hour ago is one they should look at before it goes.
 */
export const AUTO_RESEND_MAX_AGE_MS = 60 * 60 * 1000;
export const UNCONFIRMED_SEND_ERROR = "Not sent? Check the chat, then Retry.";

/** One that was on its way when the page went goes again, as the same
 * request, if it is recent. An older one shows "Not sent?" with Retry while
 * its chat is read back: its echo, if it landed, settles it. Accepted and
 * failed ones wait (for the echo, for Retry). */
function redeliverInFlight(now = Date.now()) {
  for (const list of Object.values(usePendingSends.getState().byChat)) {
    for (const send of list) {
      if (send.state !== "sending") continue;
      if (now - send.createdAt < AUTO_RESEND_MAX_AGE_MS) {
        void deliverPendingSend(queryClient, send);
        continue;
      }
      usePendingSends.getState().fail(send.chatId, send.clientRequestId, UNCONFIRMED_SEND_ERROR);
      recoverPendingSendChat(send.chatId);
    }
  }
}

function scheduleWrite() {
  if (leaving || !scope || writeTimer) return;
  writeTimer = setTimeout(() => {
    writeTimer = null;
    flushDrafts();
  }, DRAFT_WRITE_DELAY_MS);
}

/** Writes what the stores hold now, synchronously. */
export function flushDrafts(): void {
  if (writeTimer) clearTimeout(writeTimer);
  writeTimer = null;
  if (leaving || !scope) return;
  writeDrafts(scope.userId, scope.orgId, snapshotDrafts());
}

function subscribe() {
  if (unsubscribers.length > 0) return;
  unsubscribers = [
    useChatComposers.subscribe((state, previous) => { if (state.composers !== previous.composers) scheduleWrite(); }),
    useDraftChats.subscribe((state, previous) => { if (state.drafts !== previous.drafts) scheduleWrite(); }),
    useHandoffDrafts.subscribe((state, previous) => { if (state.drafts !== previous.drafts || state.front !== previous.front) scheduleWrite(); }),
    useNewTaskDraft.subscribe((state, previous) => { if (state.draft !== previous.draft) scheduleWrite(); }),
    // A pending send's state is written at once: a reload a moment after a
    // send must find it.
    usePendingSends.subscribe((state, previous) => { if (state.byChat !== previous.byChat) flushDrafts(); }),
  ];
}

/** Restored in-flight sends wait for the session to confirm the member. */
let redeliveryDue = false;

/**
 * The organization whose drafts the stores hold; switching writes the one
 * being left first. `confirmed` says the session has named this member: the
 * page load primes the stores from the last member before it answers, but
 * nothing is sent on their behalf until it does.
 */
export function activateDrafts(userId: string, orgId: string, { confirmed = false }: { confirmed?: boolean } = {}): void {
  installPageHandlers();
  if (scope?.userId !== userId || scope.orgId !== orgId) {
    flushDrafts();
    scope = null;
    revokePreviews();
    hydrate(userId, orgId);
    scope = { userId, orgId };
    leaving = false;
    redeliveryDue = true;
    subscribe();
    useDraftScope.setState({ userId, orgId });
  }
  if (confirmed && redeliveryDue) {
    redeliveryDue = false;
    redeliverInFlight();
  }
}

/** Drops what is held for chats and tasks this member can no longer see
 * (access.changed); the write that follows takes them off disk too. */
export function forgetDraftsForChats(chatIds: readonly string[], worktreeIds: readonly string[]): void {
  const lostChats = new Set(chatIds);
  const lostWorktrees = new Set(worktreeIds);
  useChatComposers.setState((state) => {
    const composers = { ...state.composers };
    for (const chatId of lostChats) {
      for (const attachment of composers[chatId]?.attachments ?? []) URL.revokeObjectURL(attachment.previewUrl);
      delete composers[chatId];
    }
    return { composers };
  });
  usePendingSends.setState((state) => {
    const byChat = { ...state.byChat };
    for (const chatId of lostChats) delete byChat[chatId];
    return { byChat };
  });
  for (const worktreeId of lostWorktrees) useDraftChats.getState().close(worktreeId);
  useHandoffDrafts.setState((state) => ({
    drafts: Object.fromEntries(Object.entries(state.drafts).filter(([key]) => !lostWorktrees.has(key.slice(0, key.indexOf(":"))) && !lostChats.has(key.slice(key.indexOf(":") + 1)))),
    front: Object.fromEntries(Object.entries(state.front).filter(([worktreeId, source]) => !lostWorktrees.has(worktreeId) && !lostChats.has(source))),
  }));
}

/**
 * A deliberate reload (protocol update, a lost organization or session) is
 * about to happen: what is unsent is written now, nothing more is written
 * after (the stores may be cleared next), and the unload guard stands down.
 * `forgetOrg` erases that organization's drafts instead — the member left it.
 */
export function prepareForReload(options: { forgetOrg?: string } = {}): void {
  flushDrafts();
  leaving = true;
  if (options.forgetOrg && scope) {
    try { storage()?.removeItem(draftsKey(scope.userId, options.forgetOrg)); } catch { /* storage unavailable */ }
  }
}

/** Sign-out: nothing unsent outlives the account on this browser. */
export function eraseAllDrafts(userId?: string): void {
  leaving = true;
  if (writeTimer) clearTimeout(writeTimer);
  writeTimer = null;
  const owner = userId ?? scope?.userId;
  const store = storage();
  if (!store || !owner) return;
  try {
    const prefix = `${KEY_PREFIX}:${owner}:`;
    const keys: string[] = [];
    for (let index = 0; index < store.length; index++) {
      const key = store.key(index);
      if (key?.startsWith(prefix)) keys.push(key);
    }
    for (const key of keys) store.removeItem(key);
  } catch {
    // Storage unavailable: nothing was written either.
  }
}

let pageHandlersInstalled = false;

/**
 * Leaving or hiding the page writes at once (a tab the browser discards gets
 * no further say). Closing it while a send is still on its way asks first:
 * the send is saved, but it has not reached the server yet.
 */
function installPageHandlers() {
  if (pageHandlersInstalled || typeof window === "undefined" || typeof window.addEventListener !== "function") return;
  pageHandlersInstalled = true;
  window.addEventListener("pagehide", flushDrafts);
  window.addEventListener("beforeunload", (event) => {
    flushDrafts();
    if (leaving || !hasSendsInFlight()) return;
    event.preventDefault();
    event.returnValue = "";
  });
  if (typeof document !== "undefined" && typeof document.addEventListener === "function") {
    document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") flushDrafts(); });
  }
}

/** Test seam. */
export function resetDraftPersistence(): void {
  if (writeTimer) clearTimeout(writeTimer);
  writeTimer = null;
  for (const unsubscribe of unsubscribers) unsubscribe();
  unsubscribers = [];
  scope = null;
  leaving = false;
  redeliveryDue = false;
  useDraftScope.setState({ userId: null, orgId: null });
}
